import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  QueryList, 
  ViewContainerRef,
  ComponentFactoryResolver,
  ComponentRef,
  ChangeDetectionStrategy } from '@angular/core';
import { ValidationErrors } from '@angular/forms';

import { DistributionList } from '../users/distribution-list.model';  
import { DynamicRowsDirective } from './dynamic-rows.directive';
import { FormField, Link } from './form-field.model';
import { FormSection } from './form-section.model';
import { FormRowComponent } from './form-row.component';
import { WorkflowValidations } from '../workflows/workflow.model';

@Component({
  selector: 'app-form-section',
  templateUrl: './form-section.component.html',
  styleUrls: ['./form-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormSectionComponent implements OnInit {
  @Input() distributionLists = [];  
  @Input() draftData: any;
  @Input() id: string;
  @Input() locked: boolean = false;
  @Input() lockValues: boolean = false;
  @Input() dynamicOverlays;
  @Input() recipients = [];
  @Input() section: FormSection;
  @Input() templateData: any;
  @Input() workflowId: number;
  @Output() atLeastOneRequiredFieldAdded = new EventEmitter<any>();
  @Output() createFileSection = new EventEmitter<{fileId: string, section: any}>();
  @Output() disclaimerChanged = new EventEmitter<{field: string, value: any}>();
  @Output() listAdded = new EventEmitter<number>();
  @Output() removeFileSection = new EventEmitter<string>();
  @Output() resized = new EventEmitter<null>();
  @Output() rowLinkAdded = new EventEmitter<{field: string, id: string, value: any}>();
  @Output() rowLinkRemoved = new EventEmitter<{field: string, id: string}>();
  @Output() rowLinkUpdated = new EventEmitter<{field: string, id: string, value: any}>();
  @Output() standardChanged = new EventEmitter<any>();
  @Output() standardConverted = new EventEmitter<any>();
  @Output() stickyHeaderAdded = new EventEmitter<any>();
  @Output() timecodesOffsetChanged = new EventEmitter<string>();
  @Output() undoableAction = new EventEmitter<any>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() valueChanged = new EventEmitter<{field: string, value: any}>();
  @Output() viewLoaded = new EventEmitter<FormSectionComponent>();
  @ViewChild(DynamicRowsDirective) dynamicRows: DynamicRowsDirective;
  @ViewChild('section') sectionElement: ElementRef;

  atLeastOneRequired: [[string]];
  dirty: boolean = false;
  errors: {type: string, id: string, label: string, errors: ValidationErrors}[] = [];
  name: string;
  rows: FormRowComponent[] = [];
  standard: any;
  subsections: FormSectionComponent[] = [];
  touched: boolean = false;
  valid: boolean = false;
  viewsLoaded: number = 0;
  workflowValidations: WorkflowValidations;

  constructor(private resolver: ComponentFactoryResolver) { }

  ngOnInit() {
    this.name = this.section.name;
    this.atLeastOneRequired = this.section.atLeastOneRequired;

    if (this.section.rows != null) {
      for (let fields of this.section.rows) {
        this.loadRow(fields);
      }
    }
    if (this.section.subsections != null) {
      for (let section of this.section.subsections) {
        this.loadSubsection(section);
      }
    }
  }

  onViewLoaded() {
    this.viewsLoaded += 1;
    if (this.viewsLoaded === (this.rows.length + this.subsections.length)) {
      this.viewLoaded.emit(this);
    }
  }

  validate() {
    let errors = [];

    let subsectionsValid = true;
    for (let section of this.subsections) {
      if (!section.valid) {
        subsectionsValid = false;
        errors.concat(section.errors);
      }
    }

    let rowsValid = true;
    for (let row of this.rows) {
      if (!row.valid) {
        rowsValid = false;
        errors.concat(row.errors);
      }
    }

    let sectionValid = true;
    if (this.atLeastOneRequired != null) {
      let atLeastOne = false;
      for (let field of this.atLeastOneRequired) {
        for (let row of this.rows) {
          for (var i = 0; i < field.length; i++) {
            let standard = row.components.find((f) => f.id === field[i]);
            if (standard != null) {
              if (standard.value.length > 0) {
                atLeastOne = true;
                break;
              }
            }
          }
        }
      }
      if (!atLeastOne) {
        sectionValid = false;
        errors.push({
          type: 'section',
          id: this.id,
          label: this.name,
          errors: {
            'atLeastOne': this.atLeastOneRequired
          }
        })
      }
    }

    this.valid = subsectionsValid && rowsValid && sectionValid;
    this.errors = !this.valid ? errors : null;
  }

  reset() {
    for (let row of this.rows) {row.reset()}
    for (let subsection of this.subsections) {subsection.reset()}
  }

  resetFiles() {
    for (let row of this.rows) {row.resetFiles()}
    for (let subsection of this.subsections) {subsection.resetFiles()}
  }

  checkDirty() {
    this.dirty = (this.rows.filter((r) => r.dirty).length > 0) ||
      (this.subsections.filter((s) => s.dirty).length > 0)
  }

  resetDirty(templateData) {
    for (let row of this.rows) {row.resetDirty(templateData)}
    for (let subsection of this.subsections) {subsection.resetDirty(templateData)}
    this.checkDirty();
  }

  checkTouched() {
    this.touched = (this.rows.filter((r) => r.touched).length > 0) ||
      (this.subsections.filter((s) => s.touched).length > 0)
  }

  loadRow(fields: FormField[]) {
    let factory = this.resolver.resolveComponentFactory(FormRowComponent);
    let container = this.dynamicRows.viewContainerRef;
    let component = container.createComponent(factory);

    let instance = component.instance
    instance.viewLoaded.subscribe(() => this.onViewLoaded());
    instance.fields = fields;
    instance.locked = this.locked;
    instance.lockValues = this.lockValues;
    instance.workflowId = this.workflowId;
    instance.templateData = this.templateData;
    instance.draftData = this.draftData;
    instance.valueChanged.subscribe((value) => {
      this.checkDirty();
      this.checkTouched();
      this.validate();
      this.valueChanged.emit(value)
    });
    instance.disclaimerChanged.subscribe((event) => {
      this.disclaimerChanged.emit(event)
    })
    instance.updateLinked.subscribe((event) => {
      this.onUpdateLinked(event, true, instance);
    })
    instance.rowLinkAdded.subscribe((event) => {
      this.onRowLinkAdded(event, true, instance)
    })
    instance.rowLinkRemoved.subscribe((event) => {
      this.onRowLinkRemoved(event, true, instance)
    })
    instance.rowLinkUpdated.subscribe((event) => {
      this.onRowLinkUpdated(event, true, instance)
    })
    instance.standard = this.standard;
    instance.createFileSection.subscribe((event) => {
      this.createFileSection.emit(event)
    })
    instance.removeFileSection.subscribe((event) => {
      this.removeFileSection.emit(event)
    })
    instance.resized.subscribe(() => {
      this.resized.emit()
    })
    instance.dynamicOverlays = this.dynamicOverlays;
    instance.listAdded.subscribe((event) => this.listAdded.emit(event));
    instance.undoableAction.subscribe((action) => this.undoableAction.emit(action));
    instance.distributionLists = this.distributionLists;
    instance.recipients = this.recipients;
    instance.stickyHeaderAdded.subscribe((header) => this.stickyHeaderAdded.emit(header));
    instance.standardChanged.subscribe((event) => this.standardChanged.emit(event));
    instance.standardConverted.subscribe((event) => this.standardConverted.emit(event));
    instance.timecodesOffsetChanged.subscribe((event) => this.timecodesOffsetChanged.emit(event));
    instance.workflowValidations = this.workflowValidations;
    instance.atLeastOneRequiredFieldAdded.subscribe((event) => this.atLeastOneRequiredFieldAdded.emit(event))

    this.rows.push(instance);
  }

  loadSubsection(section: FormSection) {
    let factory = this.resolver.resolveComponentFactory(FormSectionComponent);
    let viewContainer = this.dynamicRows.viewContainerRef;
    let component = viewContainer.createComponent(factory);

    let instance = component.instance;
    instance.viewLoaded.subscribe(() => this.onViewLoaded());
    instance.section = section;
    instance.locked = this.locked;
    instance.lockValues = this.lockValues;
    instance.workflowId = this.workflowId;
    instance.templateData = this.templateData;
    instance.draftData = this.draftData;
    instance.valueChanged.subscribe((value) => {
      this.checkDirty();
      this.checkTouched();
      this.validate();
      this.valueChanged.emit(value)
    });
    instance.disclaimerChanged.subscribe((event) => {
      this.disclaimerChanged.emit(event)
    })
    instance.updateLinked.subscribe((event) => {
      this.onUpdateLinked(event, true, instance);
    })
    instance.rowLinkAdded.subscribe((event) => {
      this.onRowLinkAdded(event, true, instance)
    })
    instance.rowLinkRemoved.subscribe((event) => {
      this.onRowLinkRemoved(event, true, instance)
    })
    instance.rowLinkUpdated.subscribe((event) => {
      this.onRowLinkUpdated(event, true, instance)
    })
    instance.standard = this.standard;
    instance.createFileSection.subscribe((event) => {
      this.createFileSection.emit(event)
    })
    instance.removeFileSection.subscribe((event) => {
      this.removeFileSection.emit(event)
    })
    instance.resized.subscribe(() => {
      this.resized.emit()
    })
    instance.dynamicOverlays = this.dynamicOverlays;
    instance.listAdded.subscribe((event) => this.listAdded.emit(event));
    instance.undoableAction.subscribe((action) => this.undoableAction.emit(action));
    instance.distributionLists = this.distributionLists;
    instance.recipients = this.recipients;
    instance.stickyHeaderAdded.subscribe((header) => this.stickyHeaderAdded.emit(header));
    instance.standardChanged.subscribe((event) => this.standardChanged.emit(event));
    instance.standardConverted.subscribe((event) => this.standardConverted.emit(event));
    instance.timecodesOffsetChanged.subscribe((event) => this.timecodesOffsetChanged.emit(event));
    instance.workflowValidations = this.workflowValidations;
    instance.atLeastOneRequiredFieldAdded.subscribe((event) => this.atLeastOneRequiredFieldAdded.emit(event))

    this.subsections.push(instance);
  }

  updateLinkedFields() {
    for (let row of this.rows) {
      row.updateLinkedFields()
    }
    for (let subsection of this.subsections) {
      subsection.updateLinkedFields()
    }
  }

  onUpdateLinked(event, propogate=false, instance?) {
    for (let r of this.rows) {
      if (instance == null || instance != r) {
        r.onUpdateLinked(event)
      }
    }
    for (let s of this.subsections) {
      if (instance == null || instance != s) {
        s.onUpdateLinked(event)
      }
    }
    if (propogate) {
      this.updateLinked.emit(event);
    }
  }

  onRowLinkAdded(event: {field: string, id: string, value: any}, propogate=false, instance?) {
    for (let r of this.rows) {
      if (instance == null || instance != r) {
        r.onRowLinkAdded(event)
      }
    }
    for (let s of this.subsections) {
      if (instance == null || instance != s) {
        s.onRowLinkAdded(event)
      }
    }
    if (propogate) {
      this.rowLinkAdded.emit(event);
    }
  }

  onRowLinkRemoved(event: {field: string, id: string}, propogate=false, instance?) {
    for (let r of this.rows) {
      if (instance == null || instance != r) {
        r.onRowLinkRemoved(event)
      }
    }
    for (let s of this.subsections) {
      if (instance == null || instance != s) {
        s.onRowLinkRemoved(event)
      }
    }
    if (propogate) {
      this.rowLinkRemoved.emit(event);
    }
  }

  onRowLinkUpdated(event: {field: string, id: string, value: any}, propogate=false, instance?) {
    for (let r of this.rows) {
      if (instance == null || instance != r) {
        r.onRowLinkUpdated(event)
      }
    }
    for (let s of this.subsections) {
      if (instance == null || instance != s) {
        s.onRowLinkUpdated(event)
      }
    }
    if (propogate) {
      this.rowLinkUpdated.emit(event);
    }
  }

  setStandard(standard) {
    this.standard = standard;
    for (let row of this.rows) {row.setStandard(standard)}
    for (let subsection of this.subsections) {subsection.setStandard(standard)}
  }

  convertStandard(standard) {
    this.standard = standard;
    for (let row of this.rows) {row.convertStandard(standard)}
    for (let subsection of this.subsections) {subsection.convertStandard(standard)}
  }

  offsetTimecodesBy(event: {offset: string, subtract: boolean}) {
    for (let row of this.rows) {row.offsetTimecodesBy(event)}
    for (let subsection of this.subsections) {subsection.offsetTimecodesBy(event)}
  }

  navigateTo() {
    this.sectionElement.nativeElement.scrollIntoView();
  }

  setPreviousValue(value) {
    for (let r of this.rows) {
      r.setPreviousValue(value);
    }
    for (let s of this.subsections) {
      s.setPreviousValue(value);
    }
  }

  resetPreviousValue() {
    for (let r of this.rows) {
      r.resetPreviousValue();
    }
    for (let s of this.subsections) {
      s.resetPreviousValue();
    }
  }

  positionOverlays() {
    for (let r of this.rows) {
      r.positionOverlays();
    }
    for (let s of this.subsections) {
      s.positionOverlays();
    }
  }

  setLockValues(state) {
    this.lockValues = state;
    for (let row of this.rows) {
      row.setLockValues(state);
    }
    for (let subsection of this.subsections) {
      subsection.setLockValues(state);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/forms/form-section.component.ts